import React, { useState } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";
import { useCart } from "../context/CartContext"; // Import the useCart hook

const ItemComponentForCart = ({ item, closeModal }) => {
    const { state, dispatch } = useCart(); 
    const initialItem = item || {
        name: "",
        price: 0,
        description: "",
        imageUrl: "",
        extras: [],
    };

    const [selectedItem, setSelectedItem] = useState({
        ...initialItem,
        quantity: 1,
        selectedExtras: [],
    });

    // Helper function to calculate the total price
    const calculateTotal = (item, quantity, selectedExtras = []) => {
        // Ensure selectedExtras is an array and calculate extrasTotal
        const extrasTotal = Array.isArray(selectedExtras) 
          ? selectedExtras.reduce((sum, extra) => sum + extra.price, 0) 
          : 0; 
        // Calculate and return the total for the item and extras
        return (item.price + extrasTotal) * quantity;
      };

    const handleExtrasChange = (extra, isSelected) => {
        setSelectedItem((prevItem) => {
            const newSelectedExtras = isSelected
                ? [...prevItem.selectedExtras, extra]
                : prevItem.selectedExtras.filter((selected) => selected._id !== extra._id);

            return {
                ...prevItem,
                selectedExtras: newSelectedExtras,
                total: calculateTotal(prevItem, prevItem.quantity, newSelectedExtras),
            };
        });
    };

    const handleQuantityChange = (change) => {
        setSelectedItem((prevItem) => {
            const newQuantity = prevItem.quantity + change;
            if (newQuantity >= 1) {
                return {
                    ...prevItem,
                    quantity: newQuantity,
                    total: calculateTotal(prevItem, newQuantity, prevItem.selectedExtras),
                };
            }
            return prevItem;
        });
    };

    // Function to handle adding item to cart
    const handleAddToCart = () => {
        const cartItem = {
            ...selectedItem,
            extras: selectedItem.selectedExtras,
            total: calculateTotal(selectedItem, selectedItem.quantity, selectedItem.selectedExtras),
        };
 
        // Dispatching the action to add the item to the cart
        dispatch({ type: "ADD_TO_CART", payload: cartItem }); 
        closeModal();
    };

    return (
        <div className="item-modal">
            {selectedItem.imageUrl && (
                <img
                    src={selectedItem.imageUrl}
                    alt={selectedItem.name}
                    className="w-full h-20 object-contain rounded-md mb-4"
                />
            )}

            <h2 className="text-xl text-gray-700 font-bold">{selectedItem.name || "Item Name"}</h2>
            <p className="text-gray-600">{selectedItem.description || "No description available."}</p>
            <p className="text-xl  text-gray-700 font-bold mb-2">${selectedItem.price.toFixed(2)}</p>

            {/* Only show extras with valid name and price */}
            {selectedItem.extras && selectedItem.extras.length > 0 && (
                <div>
                    <h5 className="font-medium mb-2">Extras:</h5>
                    {selectedItem.extras
                        .filter((extra) => extra.name && typeof extra.price === 'number')
                        .map((extra) => (
                            <div key={extra._id} className="flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    id={extra._id}
                                    className="bg-white"
                                    value={extra._id}
                                    onChange={(e) => handleExtrasChange(extra, e.target.checked)}
                                    style={{ width: '20px', height: '20px' }}
                                />
                                <label htmlFor={extra._id} className="ml-2">
                                    {extra.name} {extra.price ? `(+ $${extra.price})` : ""}
                                </label>
                            </div>
                        ))}
                </div>
            )}

            <div className="flex items-center mt-4">
                <h5 className="font-medium mb-2 mr-3">Quantity: </h5>
                <button
                    className="bg-blue-500 py-1 px-3 rounded-r text-white hover:bg-gray-400 transition duration-200"
                    onClick={() => handleQuantityChange(-1)}
                >
                    <FaMinus />
                </button>
                <input
                    type="number"
                    value={selectedItem.quantity}
                    readOnly
                    className="w-12 bg-white text-black border border-gray-300 rounded-lg pl-2 focus:outline-none focus:ring-2 focus:ring-blue-5000"
                />
                <button
                    className="bg-blue-500 py-1 px-3 rounded-r text-white hover:bg-gray-400 transition duration-200"
                    onClick={() => handleQuantityChange(1)}
                >
                    <FaPlus />
                </button>
            </div>

            <div className="mt-4">
                <label
                    htmlFor="notes"
                    className="block text-gray-700 font-medium mb-2"
                >
                    Notes:
                </label>
                <textarea
                    id="notes"
                    rows="3"
                    value={selectedItem.notes}
                    className="w-full bg-white text-black border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500-"
                    placeholder="Add any special instructions or notes here..."
                    onChange={(e) => setSelectedItem({ ...selectedItem, notes: e.target.value })}
                ></textarea>
            </div>

            <button
                className="mt-4 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
                onClick={handleAddToCart} // Call the new function
            >
                Add to Cart (${calculateTotal(selectedItem, selectedItem.quantity, selectedItem.selectedExtras).toFixed(2)})
            </button>
            <button
                className="mt-2 ml-2 bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition duration-200"
                onClick={closeModal}
            >
                Close
            </button>
        </div>
    );
};

export default ItemComponentForCart;
